// ⚠️ **Ganz nach vorn, vor jedem anderen eigenen Import:** was vorher ungefangen durchfällt,
// ist nicht mitzubekommen. Am iPad gehen die Zeilen über die Konsole nach `Documents/log.txt`
// (`ios/App/App/GeraetelogPlugin.swift`).
import { fangeAbstuerze } from './platform/absturz';
import { starteFehlerberichte } from './platform/fehlerberichte';

fangeAbstuerze(window);
starteFehlerberichte();

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { beobachteTastatur } from './platform/tastatur';

import { App } from './app/App';
import { useLocaleStore } from './i18n';
import { startAutosave } from './persistence/autosave';
import { hydrate, type HydrationResult } from './persistence/hydrate';
import { saveAssessment } from './persistence/repos';
import { getStore } from './persistence/store';
import { appendAppLog } from './platform/appLog';
import { leseEinstellungen, Settings } from './platform/einstellungen';
import { meldeErlaubnis } from './platform/fehlerberichte';
import { desktopBruecke, istGeraet } from './platform/gleis';
import { APP_VERSION_MIT_BUILD, BUILD_DATE, formatiereZeitpunkt, setGeraeteVorgabe } from './platform/appVersion';
import { ermittleBetriebssystem } from './platform/betriebssystem';
import { umgebungJetzt, vorgabeGeraetename } from './platform/geraetename';
import { raeumeArchiv } from './platform/archive';
import { setBeschriftungsQuelle, setSchalterQuelle } from './platform/fotos';
import { meldeRueckstand } from './features/header/rueckstandMelden';
import { setzeOrdnerMelder } from './platform/ordner';
import { pruefePfadlaenge } from './platform/pfadlaenge';
import { Signing } from './platform/signing';
import { verbergeSplash } from './platform/splash';
import { starteDemoWache } from './server/demoSchalter';
import { starteVorschauEinstiege } from './preview/previewEntry';
import { startPreview } from './preview/startPreview';
import { mischeEinstellungenNeu } from './store/einstellungenMischen';
import { selectAssessment, useAppStore } from './store/useAppStore';
import { setzeAutoUploadQuelle } from './platform/anstoss';
import { useToastStore } from './ui/Toast';
import './styles/fonts.css';
import './styles/global.css';

const gefunden = document.getElementById('root');
if (!gefunden) throw new Error('Kein #root im Dokument — index.html prüfen.');
// Eigener, bereits als nicht-`null` typisierter Name: die Prüfung oben schmälert `gefunden`
// nur in diesem Gültigkeitsbereich, nicht in `start()` weiter unten — dort bliebe der Typ
// sonst `HTMLElement | null`, obwohl zur Laufzeit längst sichergestellt ist, dass er es nicht ist.
const container: HTMLElement = gefunden;

/**
 * Der Stand, mit dem die App hochkommt, wenn die Hydrierung **als Ganzes** scheitert —
 * `store.open()`, die Gerätezeile und die Einstellungen liegen außerhalb der beiden inneren
 * `try`-Blöcke in `hydrate()` (s. dortiger Kommentar) und reißen sonst das ganze Versprechen
 * mit, statt wie ein Lesefehler der Prüfung selbst abgefangen zu werden.
 *
 * ⚠️ **Entscheidung:** derselbe leere Stand, den `hydrate()` auch für einen Lesefehler der
 * Prüfung liefert (`exam: null`) — **nicht** die eingebauten Beispieldaten. Mit den
 * Beispieldaten sähe der Bogen normal aus, während im Hintergrund nichts mehr gesichert
 * würde, weil `examId`/`candidateId` auf Zeilen zeigten, die es in der Ablage nie gab — genau
 * das darf dem Prüfer nicht passieren.
 *
 * ⚠️ **`leseFehler: true` gehört dazu.** Das Feld unterscheidet „nichts zu lesen" von „nicht
 * lesbar" (s. `hydrate.ts`). Scheitert die Hydrierung als Ganzes, ist unbekannt, was in der
 * Ablage steht — dann gilt sie als kaputt, und der Prüfer bekommt die Meldung statt einer
 * Oberfläche, die nichts sichern kann.
 */
const LEERER_STAND: HydrationResult = {
  client: {
    id: '',
    deviceIdentifier: '',
    deviceName: null,
    clientType: 'UNKNOWN',
    appVersion: null,
    osVersion: null,
    currentExamID: null,
    currentCandidateID: null,
    currentExaminerID: null,
    currentActorID: null,
    currentStatus: 'INACTIVE',
    online: 'N',
    batteryLevelPercent: null,
    voltageStatus: null,
    freeSpaceKb: null,
  },
  settings: {},
  exam: null,
  index: new Map(),
  assessment: null,
  lastStationId: null,
  statuses: {},
  abgesendet: {},
  boegen: {},
  sheet: { kind: 'none' },
  resumed: false,
  leseFehler: true,
};

/**
 * Rückfalluhr für die Startgrafik.
 *
 * ⚠️ Sie ist die Versicherung gegen den einen Fall, den `start()` nicht abfängt: bleibt eine
 * Zusage in `hydrate()` *hängen* (statt zu scheitern), läuft weder der Erfolgs- noch der
 * Fehlerzweig, und ohne diese Uhr stünde die Grafik für immer. Sechs Sekunden sind länger
 * als jeder gemessene Start und kurz genug, dass niemand sie für einen Absturz hält.
 */
const SPLASH_RUECKFALL_MS = 6000;
setTimeout(verbergeSplash, SPLASH_RUECKFALL_MS);

/**
 * Meldet Fassung und Baudatum an die iOS-Einstellungen (dort Gruppe „Über").
 *
 * Vor dem Hydrieren und nebenher: es hängt an nichts, und ein Fehlschlag — im Browser gibt es
 * die Einstellungsseite gar nicht — darf den Start nicht aufhalten.
 */
function meldeFassung(): void {
  void Signing.setAppInfo({
    version: APP_VERSION_MIT_BUILD,
    build: formatiereZeitpunkt(BUILD_DATE),
  }).catch((fehler: unknown) =>
    appendAppLog('Fassung an die iOS-Einstellungen melden gescheitert: ' + String(fehler), 'warn'),
  );
}

/**
 * Schreibt beim Start ins Dateilog, welche Einstellung woher kommt.
 *
 * **Wozu.** Wenn am Gerät etwas nicht so arbeitet, wie die Einstellungsseite aussieht, ist die
 * erste Frage immer dieselbe: Welche Quelle hat gewonnen — die Geräteverwaltung, die
 * iPad-Einstellungen oder die App? Ohne diese Zeile ist das am fremden iPad nicht zu klären.
 *
 * Es ist zugleich die Probe, dass `SettingsPlugin` überhaupt angemeldet ist. Genau daran ist
 * `PhotosPlugin` monatelang gescheitert, ohne dass es jemandem auffiel: die Aufrufe schlugen
 * mit „not implemented" fehl, und der Schalter liess sich trotzdem umlegen.
 *
 * ⚠️ **Werte von Passwörtern stehen hier nicht** — nur, ob eines gesetzt ist. Das Log geht
 * über die iTunes-Freigabe vom Gerät und wandert notfalls zum Server.
 */
function meldeEinstellungen(): void {
  void leseEinstellungen()
    .then((gemischt) => {
      const zeilen = Object.entries(gemischt).map(([name, e]) => {
        const geheim = name.endsWith('password');
        const wert = geheim ? (e.wert === '' ? '—' : 'gesetzt') : String(e.wert);
        return name + '=' + wert + ' (' + e.quelle + ')';
      });
      return appendAppLog('Einstellungen: ' + zeilen.join(', '));
    })
    .catch((fehler: unknown) => appendAppLog('Einstellungen lesen gescheitert: ' + String(fehler), 'warn'));
}

/**
 * Sagt der Ablage, wie aus Kennungen Namen werden — für die Beschriftung der Kopien in der
 * Fotos-Mediathek.
 *
 * ⚠️ Der Weg über einen Rückruf ist Absicht: `platform/` darf nichts aus `store/` holen
 * (s. `platform/appVersion.ts`). Die Abhängigkeit zeigt dadurch von hier nach dort und nicht
 * umgekehrt.
 */
function meldeBeschriftung(): void {
  setBeschriftungsQuelle((subject) => {
    const s = useAppStore.getState();
    const exam = s.exam;
    const station = exam?.stations.find((st) => st.id === subject.stationId);
    const aufgabe = station?.tasks.find((t) => t.id === subject.taskId);
    return {
      pruefung: exam?.name ?? '',
      station: station?.label ?? '',
      aufgabe: aufgabe?.title ?? '',
      pruefling: exam?.candidates.find((c) => c.id === subject.candidateId)?.name ?? '',
      pruefer: exam?.examiners.find((p) => p.id === subject.examinerId)?.name ?? '',
    };
  });

  // ⚠️ **Derselbe Umweg, aus einem zweiten Grund.** Die Ablage las die zwei Schalter der
  // Fotos-Kopie früher unmittelbar aus den iPad-Einstellungen und war damit die einzige Stelle
  // der App, an der die Rangfolge **nicht** galt: ein gescannter QR-Code setzte jede
  // Einstellung ausser diesen beiden. Über den Store gilt jetzt auch hier QR → MDM → iPad →
  // App → Vorgabe (Vorgabe des Nutzers, 2026-09-15).
  //
  // Gelesen wird bei **jeder** Aufnahme neu, nicht einmal beim Start: der Prüfer kann
  // zwischendurch einen Code scannen, und die nächste Kopie soll sich danach richten.
  setSchalterQuelle(() => {
    const s = useAppStore.getState();
    return { kopieren: s.fotosKopie, beschriften: s.medienBeschriftung };
  });
}

async function start(): Promise<void> {
  // Als erstes, noch vor jeder Ablage: ein Ordner, der sich nicht anlegen lässt, lässt jede
  // Aufnahme scheitern, und bis zum 2026-09-21 stand darüber nirgends etwas (`ordner.ts`).
  // Der Melder wird hineingereicht statt importiert — `appLog.ts` ruft selbst
  // `stelleOrdnerSicher`, ein Import dort wäre ein Kreis.
  setzeOrdnerMelder(appendAppLog);

  // Vor der Hydrierung: sie trägt den Namen schon in die `Client`-Zeile ein.
  setGeraeteVorgabe(await vorgabeGeraetename(umgebungJetzt()).catch(() => ''));
  // Vor jeder Anmeldung: Relay verlangt `osVersion` (seit `e964dbe`).
  await ermittleBetriebssystem();
  meldeFassung();
  meldeEinstellungen();
  meldeBeschriftung();
  // `auto_upload` gilt nach der Rangfolge; die Takte fragen hier nach, nicht im Store.
  setzeAutoUploadQuelle(() => useAppStore.getState().autoUpload);

  let stand: HydrationResult;
  try {
    // Erst hydrieren, dann rendern: sonst zeigt der Bogen einen leeren Stand, der eine Sekunde
    // später vom wiederhergestellten überschrieben wird — das sähe wie Datenverlust aus.
    stand = await hydrate();
  } catch (fehler) {
    await appendAppLog('Hydrierung insgesamt fehlgeschlagen: ' + String(fehler), 'error');
    stand = LEERER_STAND;
  }

  useAppStore.getState().applyHydration(stand);

  // Danach der gemischte Stand: Geräteverwaltung vor iPad-Einstellungen vor Ablage
  // (`platform/einstellungen.ts`). Bewusst **nach** dem Hydrieren — die Ablage ist dabei die
  // unterste Quelle, das Ergebnis also niemals ärmer als das, was gerade gesetzt wurde. Im
  // Browser kommt genau dasselbe heraus, was schon dastand.
  try {
    // ⚠️ Die gescannten Werte stehen **über** allem, auch über dem MDM (Entscheidung des
    // Nutzers, 2026-09-13, s. `platform/einstellungen.ts`). Sie kommen aus derselben Ablage
    // wie die App-Einstellungen, nur unter eigenem Vorsatz.
    // Die Sprache folgt daraus in `App.tsx` (`features/sprache.ts`).
    await mischeEinstellungenNeu();
  } catch (fehler) {
    await appendAppLog('Einstellungen übernehmen gescheitert: ' + String(fehler), 'warn');
  }

  // Fehlerberichte nach der Rangfolge, und nie aus einer Simulationsprüfung (Nutzer, 2026-10-19).
  const fehlerberichteErlaubt = (): void => {
    const s = useAppStore.getState();
    const an = s.fehlerberichte && s.exam?.simulativ !== true;
    // Eigener Schlüssel für Swift, nicht `error_reporting`: der gehört dem Benutzer.
    if (meldeErlaubnis(an) && istGeraet()) {
      void Settings.set({ key: 'error_reporting_wirksam', value: an }).catch(() => undefined);
    }
  };
  fehlerberichteErlaubt();
  useAppStore.subscribe(fehlerberichteErlaubt);

  // Die Einstellungen einer Prüfung gelten nur, solange sie ausgewählt ist (2026-10-10).
  useAppStore.subscribe((jetzt, vorher) => {
    if (jetzt.examId === vorher.examId) return;
    mischeEinstellungenNeu().catch((fehler: unknown) =>
      appendAppLog('Einstellungen nach Prüfungswechsel gescheitert: ' + String(fehler), 'warn'),
    );
  });

  if (stand.leseFehler) {
    // ⚠️ **Nur beim Lesefehler, nicht bei leerer Ablage.** Ein Gerät ohne Prüfung ist seit dem
    // 2026-09-12 der gewöhnliche Anfangszustand (die Beispielprüfungen kommen nur noch auf
    // Knopfdruck, s. `persistence/hydrate.ts`) — dort montiert `<App />` ganz normal und zeigt
    // die leere Auswahl, von der aus der Prüfer eine Prüfung holt.
    //
    // Liegt dagegen eine Prüfung, die sich nicht lesen lässt, ist bewusst kein `<App />` da:
    // fast jede Anzeige liest über kurz oder lang `selectExam`, und das wirft ohne Prüfung
    // ausdrücklich (s. `useAppStore.ts`). Eine leere, erklärende Meldung ist dem Prüfer
    // ehrlicher als ein Bildschirm, der aussieht, als liefe die Bewertung, während nichts
    // mehr gesichert wird.
    //
    // Die App führt drei Sprachen, und dies ist die einzige Meldung, die ein Prüfer im
    // Fehlerfall überhaupt zu lesen bekommt — kein Grund, sie als einzige hartkodiert
    // Deutsch zu lassen. `App`s eigener `initLocale()`-Aufruf läuft nicht, weil `<App />`
    // hier nie montiert wird; `init()` selbst hängt nicht an der (gerade gescheiterten)
    // Ablage, sondern an `Preferences`/`Device`, deshalb lässt es sich hier gefahrlos direkt
    // aufrufen.
    await useLocaleStore.getState().init();
    container.textContent = useLocaleStore.getState().t.persistence.startupFailed;
    // Ohne dies bliebe die Startgrafik über der Meldung stehen — der Prüfer sähe nie, dass
    // etwas schiefging, sondern nur eine App, die nicht hochkommt.
    verbergeSplash();
    return;
  }

  startAutosave({
    read: () => {
      const s = useAppStore.getState();
      return {
        ctx: { examID: s.examId, candidateID: s.candidateId, examinerID: s.examinerId, actorID: s.actorId },
        assessment: selectAssessment(s),
        // Ziel des Stationskommentars. Er wandert im selben Zug wie die Bewertung ins
        // Protokoll — aus demselben `read()`, damit er nicht zu einer anderen Station
        // geschrieben wird als der, an der gerade bewertet wurde.
        stationId: s.stationId,
      };
    },
    // `nachher.ctx` — nicht noch einmal `useAppStore.getState()`: der Bezug kommt aus
    // demselben `read()`-Aufruf wie die Bewertung, mit der er geschrieben wird (s. Fix-Runde
    // zu Aufgabe 17, `autosave.ts`). `taskIndex` bleibt frisch aus dem Store, weil ein
    // Prüfungswechsel bereits vorher sichert und den Index synchron mit `examId` tauscht
    // (`useAppStore.ts::selectExam`) — dort öffnet sich dasselbe Fenster nicht.
    save: async (vorher, nachher) => {
      const ergebnis = await saveAssessment(
        getStore(),
        useAppStore.getState().taskIndex,
        nachher.ctx,
        vorher.assessment,
        nachher.assessment,
        nachher.stationId,
      );
      /*
       * Gleich nachzählen, statt bis zum nächsten Takt zu warten: der Prüfer soll die Zahl in
       * der Kopfzeile steigen sehen, während er bewertet — sonst wirkt sie wie eine Anzeige,
       * die nicht mitbekommt, was er tut. Die zwei Abfragen kosten nichts gegen das Schreiben
       * davor, und der Aufruf wirft nicht (s. `rueckstandMelden.ts`).
       */
      void meldeRueckstand();
      return ergebnis;
    },
    log: appendAppLog,
    toast: (schlüssel) => useToastStore.getState().show(schlüssel),
    subscribe: (fn) => useAppStore.subscribe(fn),
  });

  // Kein eigener `appStateChange`-Horcher mehr an dieser Stelle: `startAutosave()` meldet
  // seinen eigenen an (`autosave.ts`, „Fängt den Wechsel in den Hintergrund auf dem Gerät ab“)
  // und ruft dieselbe `flushNow()`. Der Horcher hier war eine Dopplung aus Aufgabe 17 — er
  // liess bei jedem Hintergrundwechsel zwei Sicherungen in dieselbe `läuft`-Kette laufen (die
  // zweite folgenlos, weil sich zwischen beiden nichts geändert hat), wurde nie wieder
  // abgemeldet und fing im Gegensatz zu dem in `autosave.ts` einen Fehlschlag beim Anmelden
  // nicht ab.
  // Das Archiv gelöschter Prüfungen räumen: Tagesordner, die älter als die eingestellte
  // Frist sind, verschwinden. Nebenher — es darf den Start nicht aufhalten, und ein
  // Fehlschlag ist kein Grund, die App nicht hochzubringen.
  void raeumeArchiv(useAppStore.getState().archivTage, appendAppLog).catch((fehler: unknown) =>
    appendAppLog('Archiv räumen gescheitert: ' + String(fehler), 'warn'),
  );

  // Unter Windows darf der ganze Pfad 260 Zeichen nicht überschreiten (`platform/pfadlaenge.ts`).
  void pruefePfadlaenge(() => useToastStore.getState().show('pfadZuLang'));

  /*
   * Der Schalter `demo_install` aus den iPad-Einstellungen: einmal jetzt, danach bei jeder
   * Rückkehr in den Vordergrund (`server/demoSchalter.ts`).
   *
   * Die Liste im Einrichtungsblatt muss dafür nicht angestossen werden — sie liest die Ablage
   * beim Öffnen (`SetupSheet.tsx`, `lokalLesen` im `useEffect`). Stand sie beim Wechsel schon
   * offen, sagt es die Einblendung, und ein Tipp auf „Aktualisieren" holt den Rest.
   *
   * ⚠️ Der Abmelder wird bewusst nicht aufgehoben: `start()` läuft genau einmal je Sitzung des
   * WebViews, und der Horcher soll so lange leben wie die App. Ein `remove()` gäbe es nur beim
   * Abbau, den es hier nicht gibt.
   */
  starteDemoWache(getStore(), stand.client.id, () =>
    useToastStore.getState().show('demoInstalled'),
  );

  // Die Tastaturhöhe als CSS-Variable an der Wurzel — die Blätter rücken im Querformat
  // darüber, statt sich darunter zu verstecken (s. `platform/tastatur.ts`). Ein Horcher für
  // die ganze App, ohne React-Zustand.
  beobachteTastatur();

  createRoot(container).render(
    <StrictMode>
      <App />
    </StrictMode>,
  );

  // Zwei Bildschirmtakte warten, nicht einen: `render()` reiht die Arbeit nur ein. Nach dem
  // ersten Takt hat React gerechnet und in den Baum geschrieben, erst nach dem zweiten hat
  // das Gerät das Ergebnis auch gemalt. Ein Takt zu früh, und zwischen Grafik und Bogen
  // blitzt der leere WebView auf — genau das, was die Grafik verhindern soll.
  requestAnimationFrame(() => requestAnimationFrame(verbergeSplash));

  // Preview per Web-Adresse oder Desktop-Aufruf — erst jetzt, wenn Ablage und Store stehen.
  // Am iPad gibt es diesen Weg nicht; dort kommt der Code über den Scan.
  void starteVorschauEinstiege(startPreview, desktopBruecke());
}

void start();
